// JURÍDICO — a lista única de contratos (2026-08-20).
//
// Duas origens no mesmo lugar, de propósito: "contrato que escrevemos" e "PDF
// que recebemos" são a mesma coisa para quem administra. O que muda é onde o
// instrumento mora — e é isso que a coluna Origem diz.
//
// 🚨 Contrato de tipo sensível (vínculo de pessoa) chega com os campos em
//    `null` e `visivel: false`. A tela mostra "reservado" em vez de inventar
//    um traço: o dado existe, quem está olhando é que não pode vê-lo.
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  App as AntApp, Button, Card, DatePicker, Empty, Flex, Form, Input, InputNumber,
  Modal, Popconfirm, Segmented, Select, Space, Table, Tag, Tooltip, Typography,
} from 'antd';
import dayjs from 'dayjs';
import {
  listarContratos, criarContrato, salvarFicha, excluirContrato, listarTiposContrato,
  abrirPdfDoContrato, obterContrato, listarContratosOpDoCliente,
  ContratoLinha, SITUACAO_CFG, RENOVACAO_CFG, Renovacao, NossoPapel,
  ContratoOpOpcao, TipoContrato, TIPO_DISTRATO, opcoesPapel, textoPapel,
} from './api';
import { ModalTiposContrato } from './ModalTiposContrato';
import { fmtDataBr } from '../../utils/dataFmt';
import { ModalIncluirAssinado } from './ModalIncluirAssinado';
import { ModalDistrato } from './ModalDistrato';
// O painel do documento finalizado é o MESMO dos Expedientes — assinar pela
// D4 Sign, acompanhar, baixar o assinado, cancelar. Ele é auto-contido (só
// precisa do id) e o modal de envio dele já pré-seleciona as PARTES quando a
// área é jurídica. Duplicar viraria segunda cópia do fluxo de assinatura.
import DrawerExpediente from '../administrativo-expedientes/DrawerExpediente';
import { listarTemplates, excluirTemplate, TemplateContrato } from './api';

const { Text, Title } = Typography;

const moeda = (v: string | null) =>
  v === null || v === undefined ? '—'
    : Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

export function PainelContratos({ soVigencias = false }: { soVigencias?: boolean }) {
  const { message } = AntApp.useApp();
  const navigate = useNavigate();

  const [carregando, setCarregando] = useState(true);
  const [contratos, setContratos] = useState<ContratoLinha[]>([]);
  const [podeCriar, setPodeCriar] = useState(false);
  const [podeEditar, setPodeEditar] = useState(false);
  const [podeExcluir, setPodeExcluir] = useState(false);
  const [origem, setOrigem] = useState<string>('todas');
  const [busca, setBusca] = useState('');

  const [tipos, setTipos] = useState<TipoContrato[]>([]);
  const [novoAberto, setNovoAberto] = useState(false);
  // TIPOS DE CONTRATO (2026-09-15): o cadastro dos tipos (mútuo, locação…).
  const [podeGerenciarTipos, setPodeGerenciarTipos] = useState(false);
  const [tiposAberto, setTiposAberto] = useState(false);
  const [assinadoAberto, setAssinadoAberto] = useState(false);
  const [distratando, setDistratando] = useState<ContratoLinha | null>(null);
  // O drawer do documento (assinatura, PDF, histórico). `?assinar=<expediente>`
  // abre direto — é onde o Finalizar do editor desembarca.
  const [drawerExp, setDrawerExp] = useState<string | null>(null);
  const [params, setParams] = useSearchParams();
  useEffect(() => {
    const alvo = params.get('assinar');
    if (alvo) {
      setDrawerExp(alvo);
      // Limpa o parâmetro para o F5 não reabrir o drawer para sempre.
      params.delete('assinar');
      setParams(params, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [ficha, setFicha] = useState<ContratoLinha | null>(null);
  // GUARDA-CHUVA (2026-08-25): o que a ficha cobre. A LISTA não traz (jur_listar
  // não devolve cobertura) — vem do jur_obter ao abrir, junto das opções do
  // cliente para poder editar.
  const [opcoesOp, setOpcoesOp] = useState<ContratoOpOpcao[]>([]);
  const [carregandoCob, setCarregandoCob] = useState(false);
  // O principal REAL da ficha (jur_obter). `ContratoLinha` não tem esse campo:
  // ler dela daria undefined e o save reelegeria o principal toda vez.
  const [principalAtual, setPrincipalAtual] = useState<string | null>(null);
  // Corrida: abrir duas fichas rápido fazia a resposta da primeira cair dentro
  // do formulário da segunda (foro, observações e a COBERTURA trocadas).
  const fichaPedidoRef = useRef(0);
  const [salvando, setSalvando] = useState(false);
  const [formNovo] = Form.useForm();
  const [formFicha] = Form.useForm();

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const r = await listarContratos({
        ...(origem !== 'todas' ? { origem } : {}),
        ...(busca ? { busca } : {}),
        // A aba Vigências é a mesma lista com uma pergunta diferente: "o que
        // exige decisão nos próximos 90 dias?".
        ...(soVigencias ? { vencendo_em_dias: 90 } : {}),
      });
      setContratos(r.contratos || []);
      setPodeCriar(!!r.pode_criar);
      setPodeEditar(!!r.pode_editar);
      setPodeExcluir(!!r.pode_excluir);
      setPodeGerenciarTipos(!!r.pode_gerenciar_tipos);
    } catch (e: any) {
      message.error('Falha ao carregar contratos: ' + (e.response?.data?.error || e.message));
    } finally { setCarregando(false); }
  }, [origem, busca, soVigencias, message]);

  useEffect(() => { carregar(); }, [carregar]);

  // Templates (minutas da casa): o Select "Começar de" no Redigir contrato.
  const [templates, setTemplates] = useState<TemplateContrato[]>([]);
  // O tipo escolhido no Redigir decide o nome das partes e as minutas que
  // fazem sentido (minuta de mútuo não serve de ponto de partida para locação).
  const tipoNovoId = Form.useWatch('tipo_id', formNovo);
  const tipoNovo = tipos.find((t) => t.id === tipoNovoId) || null;
  const templatesDoTipo = templates.filter((t) => !t.tipo_id || !tipoNovoId || t.tipo_id === tipoNovoId);

  async function abrirNovo() {
    try {
      const r = await listarTiposContrato();
      // Redigir oferece só o que se redige aqui: seguro e software entram por
      // upload, e o distrato tem o botão próprio em cada contrato.
      setTipos((r.tipos || []).filter((t) => t.redigivel && t.codigo !== TIPO_DISTRATO));
      // Falha ao listar templates não trava o redigir: sem minuta, o corpo
      // padrão do tipo continua servindo de partida.
      try { setTemplates((await listarTemplates()).templates || []); }
      catch { setTemplates([]); }
      formNovo.resetFields();
      setNovoAberto(true);
    } catch (e: any) {
      message.error('Falha ao carregar os tipos: ' + (e.response?.data?.error || e.message));
    }
  }

  async function removerTemplate(id: number) {
    try {
      await excluirTemplate(id);
      setTemplates(ts => ts.filter(t => t.id !== id));
      if (formNovo.getFieldValue('template_id') === id) {
        formNovo.setFieldValue('template_id', undefined);
      }
      message.success('Template excluído.');
    } catch (e: any) {
      message.error('Falha ao excluir: ' + (e.response?.data?.error || e.message));
    }
  }

  async function criar() {
    let v: any;
    try { v = await formNovo.validateFields(); } catch { return; }
    setSalvando(true);
    try {
      const r = await criarContrato({
        ...v,
        vigencia_inicio: v.vigencia_inicio ? dayjs(v.vigencia_inicio).format('YYYY-MM-DD') : null,
        vigencia_fim:    v.vigencia_fim ? dayjs(v.vigencia_fim).format('YYYY-MM-DD') : null,
      });
      setNovoAberto(false);
      // O editor é o MESMO dos expedientes — inclusive a Pam, que já redige.
      navigate(`/administrativo/expedientes/${r.expediente_id}`);
    } catch (e: any) {
      message.error('Falha ao criar: ' + (e.response?.data?.error || e.message));
    } finally { setSalvando(false); }
  }

  async function abrirFicha(c: ContratoLinha) {
    setFicha(c);
    // resetFields ANTES do setFieldsValue: o form vive entre aberturas e
    // campos que a LISTA não traz (foro, observações, cobertura) ficariam com
    // o valor da ficha anterior.
    formFicha.resetFields();
    formFicha.setFieldsValue({
      ...c,
      valor: c.valor === null ? null : Number(c.valor),
      vigencia_inicio: c.vigencia_inicio ? dayjs(c.vigencia_inicio) : null,
      vigencia_fim: c.vigencia_fim ? dayjs(c.vigencia_fim) : null,
    });
    // A cobertura e os campos completos vêm do jur_obter — a linha da lista é
    // um resumo. Falha aqui não fecha a ficha: os campos da lista já valem.
    const pedido = ++fichaPedidoRef.current;
    setCarregandoCob(true);
    setOpcoesOp([]);
    setPrincipalAtual(null);
    try {
      const d = await obterContrato(c.id);
      if (pedido !== fichaPedidoRef.current) return;   // outra ficha foi aberta
      const cobertos = (d?.cobertura || []).map((x) => x.contrato_op_id);
      setPrincipalAtual(d?.contrato?.contrato_op_id ?? null);
      formFicha.setFieldsValue({
        foro: d?.contrato?.foro ?? undefined,
        observacoes: d?.contrato?.observacoes ?? undefined,
        aviso_previa_dias: d?.contrato?.aviso_previa_dias ?? undefined,
        cobre_contratos_op: cobertos,
      });
      // As opções do picker são os contratos do cliente da ficha. Sem cliente
      // (legado), mostra só o que já está coberto — não dá para oferecer mais.
      const clienteId = d?.contrato?.cliente_id;
      if (clienteId) {
        const o = await listarContratosOpDoCliente(Number(clienteId));
        if (pedido !== fichaPedidoRef.current) return;
        // Junta o que já está coberto com o que o cliente oferece: projeto
        // coberto que a lista não devolve (cancelado, ou de outro cliente)
        // apareceria como UUID cru na tag e poderia sumir num save.
        const doCliente = o?.contratos || [];
        const faltando = (d?.cobertura || [])
          .filter((x) => !doCliente.some((y) => y.id === x.contrato_op_id))
          .map((x) => ({
            id: x.contrato_op_id, numero: x.numero, titulo: x.titulo || '',
            status: x.status || '', contrato_pai_id: null, pai_numero: null,
            valor_contratado: null, data_inicio_prevista: null,
            data_termino_previsto: null, coberto_por: null, coberto_por_nome: null,
          }));
        setOpcoesOp([...doCliente, ...faltando]);
      } else {
        setOpcoesOp((d?.cobertura || []).map((x) => ({
          id: x.contrato_op_id, numero: x.numero, titulo: x.titulo || '',
          status: x.status || '', contrato_pai_id: null, pai_numero: null,
          valor_contratado: null, data_inicio_prevista: null,
          data_termino_previsto: null, coberto_por: null, coberto_por_nome: null,
        })));
      }
    } catch { /* a ficha abre mesmo assim, só sem a cobertura editável */ }
    finally { setCarregandoCob(false); }
  }

  async function salvar() {
    if (!ficha) return;
    let v: any;
    try { v = await formFicha.validateFields(); } catch { return; }
    setSalvando(true);
    try {
      await salvarFicha({
        id: ficha.id, ...v,
        vigencia_inicio: v.vigencia_inicio ? dayjs(v.vigencia_inicio).format('YYYY-MM-DD') : null,
        vigencia_fim:    v.vigencia_fim ? dayjs(v.vigencia_fim).format('YYYY-MM-DD') : null,
        // Lista vazia não vai: o backend recusa cobertura vazia (400), e ficha
        // sem projeto nenhum é o estado NORMAL de metade do acervo — salvar o
        // foro dela não pode virar erro. Para DESvincular, use o projeto
        // principal na Visão geral do contrato.
        cobre_contratos_op: (v.cobre_contratos_op || []).length
          ? v.cobre_contratos_op : undefined,
        // O principal só muda quando PRECISA: se o atual continua coberto, ele
        // fica. Reeleger a cada save trocaria o projeto principal da ficha
        // (o que aparece no card da Visão geral) sem ninguém ter pedido.
        contrato_op_id: (v.cobre_contratos_op || []).length
          ? ((v.cobre_contratos_op as string[]).includes(String(principalAtual))
              ? principalAtual : v.cobre_contratos_op[0])
          : undefined,
      });
      message.success('Ficha atualizada.');
      setFicha(null);
      carregar();
    } catch (e: any) {
      message.error('Falha ao salvar: ' + (e.response?.data?.error || e.message));
    } finally { setSalvando(false); }
  }

  async function abrirPdf(c: ContratoLinha) {
    if (!c.documento_id) return;
    try {
      const url = await abrirPdfDoContrato(c.documento_id);
      // Aba nova: o link é assinado e expira em 10 minutos — navegar na mesma
      // aba faria a pessoa perder a lista para voltar a um link morto.
      window.open(url, '_blank', 'noopener');
    } catch (e: any) {
      message.error('Não consegui abrir o PDF: ' + (e.response?.data?.error || e.message));
    }
  }

  async function excluir(c: ContratoLinha) {
    try {
      await excluirContrato(c.id);
      message.success('Ficha excluída.');
      carregar();
    } catch (e: any) {
      const st = e?.response?.status;
      message.error(st === 409
        ? 'Contrato já finalizado — cancele em vez de excluir.'
        : 'Falha ao excluir: ' + (e.response?.data?.error || e.message));
    }
  }

  const resumo = {
    vencidos: contratos.filter((c) => c.situacao_calc === 'vencido').length,
    vencendo: contratos.filter((c) => c.situacao_calc === 'vencendo').length,
    // O que mais dói: renova sozinho e ninguém percebeu a tempo.
    automaticos: contratos.filter((c) => c.renovacao === 'automatica'
      && (c.situacao_calc === 'vencendo' || c.situacao_calc === 'vencido')).length,
  };

  const colunas = [
    {
      title: 'Contraparte', dataIndex: 'contraparte_nome', key: 'contraparte', width: '26%',
      render: (_: any, c: ContratoLinha) => (
        <Space direction="vertical" size={0}>
          {c.visivel
            ? <Text strong>{c.contraparte_nome}</Text>
            : <Tooltip title="Contrato com dado pessoal. Só quem participa vê a contraparte e o valor.">
                <Text type="secondary" italic>reservado</Text>
              </Tooltip>}
          <Text type="secondary" style={{ fontSize: 11 }}>
            {c.codigo || 'sem número'} · {c.tipo_nome || '—'}
          </Text>
          {/* As duas pontas do distrato aparecem na mesma lista: o papel diz o
              que encerra, e o contrato diz que foi encerrado (e desde quando —
              distrato com efeito futuro ainda não rescindiu nada). */}
          {c.instrumento === 'distrato' && (
            <Tag color="volcano" style={{ fontSize: 10, marginTop: 2 }}>
              distrato de {c.pai_codigo || 'um contrato'}
            </Tag>
          )}
          {/* Espelho do envelope (o cron atualiza) — quem está esperando o
              cliente assinar vê daqui, sem abrir o drawer. */}
          {c.visivel && c.assinatura_status === 'aguardando_assinatura' && (
            <Tag color="processing" style={{ fontSize: 10, marginTop: 2 }}>aguardando assinatura</Tag>
          )}
          {c.visivel && c.assinatura_status === 'processando_envio' && (
            <Tag color="processing" style={{ fontSize: 10, marginTop: 2 }}>enviando…</Tag>
          )}
          {c.visivel && c.assinatura_status === 'assinado' && (
            <Tag color="green" style={{ fontSize: 10, marginTop: 2 }}>assinado</Tag>
          )}
          {/* Envio que falhou NÃO pode parecer "tudo certo" — a regra da casa é
              que falha nunca vira "nada novo". */}
          {c.visivel && c.assinatura_status === 'erro_envio' && (
            <Tag color="red" style={{ fontSize: 10, marginTop: 2 }}>envio falhou</Tag>
          )}
          {c.instrumento === 'contrato' && c.distrato_id && (
            <Tooltip title={c.distrato_em && c.distrato_em > new Date().toISOString().slice(0, 10)
              ? 'Distrato assinado; o contrato vale até a data de efeito.'
              : 'Encerrado por distrato.'}>
              <Tag color="volcano" style={{ fontSize: 10, marginTop: 2 }}>
                distratado{c.distrato_em ? ` em ${fmtDataBr(c.distrato_em)}` : ''}
              </Tag>
            </Tooltip>
          )}
          {c.visivel && c.projeto_numero && (
            <Text type="secondary" style={{ fontSize: 11 }}>projeto {c.projeto_numero}</Text>
          )}
        </Space>
      ),
    },
    {
      title: 'Origem', dataIndex: 'origem', key: 'origem', width: 120,
      render: (o: string) => o === 'redigido'
        ? <Tag color="blue">nossa minuta</Tag>
        : <Tag>de terceiro</Tag>,
    },
    {
      title: 'Papel', dataIndex: 'nosso_papel', key: 'papel', width: 150,
      // O nome do lado vem do TIPO da linha: num mútuo, "Somos Mutuante".
      render: (p: NossoPapel | null, c: ContratoLinha) => p
        ? <Text style={{ fontSize: 12 }}>{textoPapel(p, c)}</Text> : '—',
    },
    {
      title: 'Valor', dataIndex: 'valor', key: 'valor', width: 130, align: 'right' as const,
      render: (v: string | null, c: ContratoLinha) => c.visivel ? moeda(v) : '—',
    },
    {
      title: 'Vigência', dataIndex: 'vigencia_fim', key: 'vigencia', width: 170,
      render: (_: any, c: ContratoLinha) => {
        if (!c.visivel) return '—';
        if (!c.vigencia_fim) return <Text type="secondary">sem prazo</Text>;
        const d = c.dias_para_vencer;
        const cor = d === null ? undefined : d < 0 ? '#B91626' : d <= 60 ? '#B07500' : undefined;
        return (
          <Space direction="vertical" size={0}>
            <Text style={{ color: cor }}>{fmtDataBr(c.vigencia_fim)}</Text>
            {d !== null && (
              <Text type="secondary" style={{ fontSize: 11 }}>
                {d < 0 ? `venceu há ${-d}d` : d === 0 ? 'vence hoje' : `faltam ${d}d`}
              </Text>
            )}
            {c.renovacao === 'automatica' && (
              <Tag color="red" style={{ fontSize: 10, marginTop: 2 }}>renova sozinho</Tag>
            )}
          </Space>
        );
      },
    },
    {
      title: 'Situação', dataIndex: 'situacao_calc', key: 'situacao', width: 120,
      render: (s: string) => <Tag color={SITUACAO_CFG[s]?.cor}>{SITUACAO_CFG[s]?.label || s}</Tag>,
    },
    {
      title: '', key: 'acoes', width: 260,
      render: (_: any, c: ContratoLinha) => (
        <Space size={4}>
          {c.expediente_id && c.visivel && c.status_expediente === 'rascunho' && (
            <Button size="small" onClick={() => navigate(`/administrativo/expedientes/${c.expediente_id}`)}>
              Abrir texto
            </Button>
          )}
          {/* Finalizado: o drawer tem tudo — enviar para a D4 Sign (com as
              partes sugeridas), acompanhar, baixar o assinado e cancelar com
              motivo. É a mesma porta dos Expedientes; contrato só não aparece
              naquela lista. */}
          {/* Gateado por `visivel` como os vizinhos: em linha sensível
              "reservada" o drawer só devolveria o erro do backend — botão que
              vira beco sem saída (achado da revisão adversarial). E o rótulo
              é honesto por estado: documento cancelado não se assina — o
              drawer ali oferece o substituto. */}
          {c.expediente_id && c.visivel && c.status_expediente
            && c.status_expediente !== 'rascunho' && (
            <Button size="small" type={c.status_expediente === 'finalizado' ? 'primary' : 'default'}
              ghost={c.status_expediente === 'finalizado'}
              onClick={() => setDrawerExp(c.expediente_id!)}>
              {c.status_expediente === 'finalizado' ? '✍️ Assinatura' : 'Documento'}
            </Button>
          )}
          {/* Contrato de terceiro e da venda existem como PDF no acervo. Desde
              que a categoria saiu da tela de Documentos da empresa (21/08),
              ESTA é a única porta para o arquivo — sem ela o contrato estaria
              guardado e inalcançável. */}
          {c.documento_id && c.visivel && (
            <Button size="small" onClick={() => abrirPdf(c)}>Abrir PDF</Button>
          )}
          {podeEditar && c.visivel && <Button size="small" onClick={() => abrirFicha(c)}>Ficha</Button>}
          {/* Distratar só faz sentido em contrato que chegou a valer e ainda
              não tem distrato. Rascunho se cancela; distrato não se distrata. */}
          {podeCriar && c.visivel && c.instrumento === 'contrato'
            && !c.distrato_id && c.situacao !== 'rascunho' && (
            <Button size="small" danger onClick={() => setDistratando(c)}>Distrato</Button>
          )}
          {podeExcluir && c.visivel && (
            <Popconfirm title="Excluir a ficha deste contrato?"
              description="O documento continua onde está. Contrato já numerado não pode ser excluído."
              okText="Excluir" okButtonProps={{ danger: true }} cancelText="Cancelar"
              onConfirm={() => excluir(c)}>
              <Button size="small" danger>Excluir</Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      {soVigencias && (
        <Flex gap={10} wrap style={{ marginBottom: 12 }}>
          {[
            { t: 'Vencidos', n: resumo.vencidos, cor: '#B91626' },
            { t: 'Vencendo em 90 dias', n: resumo.vencendo, cor: '#B07500' },
            { t: 'Renovam sozinhos', n: resumo.automaticos, cor: '#B91626' },
          ].map((c) => (
            <Card key={c.t} size="small" style={{ minWidth: 180, flex: '1 1 180px' }}>
              <Text type="secondary" style={{ fontSize: 12 }}>{c.t}</Text>
              <Title level={3} style={{ margin: 0, color: c.n ? c.cor : undefined }}>{c.n}</Title>
            </Card>
          ))}
        </Flex>
      )}

      <Flex justify="space-between" align="center" wrap gap={8} style={{ marginBottom: 10 }}>
        <Space wrap>
          {!soVigencias && (
            <Segmented value={origem} onChange={(v) => setOrigem(String(v))}
              options={[
                { label: 'Todos', value: 'todas' },
                { label: 'Nossas minutas', value: 'redigido' },
                { label: 'De terceiros', value: 'terceiro' },
              ]} />
          )}
          <Input.Search placeholder="Contraparte, código ou objeto" allowClear
            style={{ width: 300 }} onSearch={setBusca}
            onChange={(e) => { if (!e.target.value) setBusca(''); }} />
        </Space>
        {podeCriar && !soVigencias && (
          <Space>
            {/* O caminho mais usado é este: o contrato JÁ existe, assinado, em
                PDF. Redigir do zero é o caso raro — por isso este vem primeiro
                e é o botão principal. */}
            <Button type="primary" onClick={() => setAssinadoAberto(true)}>
              Incluir contrato assinado
            </Button>
            <Button onClick={abrirNovo}>Redigir contrato</Button>
            {podeGerenciarTipos && (
              <Button onClick={() => setTiposAberto(true)}>Tipos de contrato</Button>
            )}
          </Space>
        )}
        {/* Quem só cadastra tipo (sem criar contrato) também chega ao catálogo. */}
        {!podeCriar && podeGerenciarTipos && !soVigencias && (
          <Button onClick={() => setTiposAberto(true)}>Tipos de contrato</Button>
        )}
      </Flex>

      <Table
        rowKey="id" size="small" loading={carregando} tableLayout="fixed"
        dataSource={contratos} columns={colunas as any} pagination={false}
        locale={{ emptyText: (
          <Empty description={soVigencias
            ? 'Nada vencendo nos próximos 90 dias'
            : 'Nenhum contrato cadastrado ainda'} />
        ) }}
      />

      {/* ── Documento finalizado: assinatura, PDF e histórico ── */}
      <DrawerExpediente
        id={drawerExp}
        onClose={() => setDrawerExp(null)}
        onMudou={carregar}
      />

      {/* ── Distrato: o papel que encerra o contrato ── */}
      <ModalDistrato
        contrato={distratando}
        onFechar={() => setDistratando(null)}
        onPronto={(r) => {
          setDistratando(null);
          carregar();
          if (r.expediente_id) navigate(`/administrativo/expedientes/${r.expediente_id}`);
        }}
      />

      {/* ── Cadastro dos tipos de contrato (mútuo, locação…) ── */}
      <ModalTiposContrato
        aberto={tiposAberto}
        onFechar={() => setTiposAberto(false)}
        onMudou={carregar}
      />

      {/* ── Contrato que já existe em PDF: a IA lê e preenche a ficha ── */}
      <ModalIncluirAssinado
        aberto={assinadoAberto}
        onFechar={() => setAssinadoAberto(false)}
        onArquivado={carregar}
      />

      {/* ── Redigir contrato ── */}
      <Modal
        open={novoAberto} onCancel={() => setNovoAberto(false)} onOk={criar}
        confirmLoading={salvando} okText="Criar e abrir o texto" cancelText="Cancelar"
        title="Redigir contrato"
        width="94vw" style={{ maxWidth: 720, top: 16 }}
        styles={{ body: { maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' } }}
      >
        <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 12 }}>
          O contrato usa o mesmo editor dos expedientes — numeração automática, assinatura
          digital e a Pam para redigir. Só recebe número quando você finalizar.
        </Text>
        <Form form={formNovo} layout="vertical" size="small">
          <Form.Item name="tipo_id" label="Tipo de contrato" rules={[{ required: true }]}>
            <Select showSearch optionFilterProp="label"
              onChange={() => {
                // Troca de tipo: a minuta escolhida pode ser de outro tipo, e o
                // papel pode estar num lado com outro nome — confere de novo.
                formNovo.setFieldValue('template_id', undefined);
                // O papel também sai: "1ª parte" do mútuo é Mutuante, e o lado
                // escolhido para outro tipo não significa o mesmo aqui.
                formNovo.setFieldValue('nosso_papel', undefined);
              }}
              options={tipos.map((t) => ({
                value: t.id, label: `${t.prefixo} — ${t.nome}`,
              }))} />
          </Form.Item>
          {/* A minuta escolhida entra no lugar do corpo padrão do tipo. Quem
              cria os templates é o próprio editor ("Salvar como template"). */}
          {templatesDoTipo.length > 0 && (
            <Form.Item name="template_id" label="Começar de"
              tooltip="O texto do contrato abre com esta minuta em vez do modelo padrão do tipo.">
              <Select allowClear placeholder="Modelo padrão do tipo"
                optionLabelProp="label"
                options={templatesDoTipo.map((t) => ({
                  value: t.id,
                  label: t.nome,
                }))}
                optionRender={(o) => {
                  const t = templates.find(x => x.id === o.value);
                  return (
                    <Flex justify="space-between" align="center">
                      <span>{t?.nome}{t?.tipo_prefixo ? ` · ${t.tipo_prefixo}` : ''}</span>
                      <Button type="text" size="small" danger
                        onClick={(e) => { e.stopPropagation(); if (t) removerTemplate(t.id); }}>
                        excluir
                      </Button>
                    </Flex>
                  );
                }} />
            </Form.Item>
          )}
          <Flex gap={12} wrap>
            <Form.Item name="contraparte_nome" label="Contraparte" rules={[{ required: true }]}
              style={{ flex: '1 1 260px' }}>
              <Input placeholder="Razão social ou nome" />
            </Form.Item>
            <Form.Item name="contraparte_doc" label="CNPJ / CPF" style={{ flex: '0 0 180px' }}>
              <Input />
            </Form.Item>
          </Flex>
          <Form.Item name="objeto" label="Objeto">
            <Input.TextArea rows={2} placeholder="Ex.: levantamento topobatimétrico do reservatório X" />
          </Form.Item>
          <Flex gap={12} wrap>
            {/* Sem valor de partida (revisão de 15/09): "Contratada" pré-marcado
                num mútuo faria a Hydrodata MUTUÁRIA — a pessoa escolhe o lado. */}
            <Form.Item name="nosso_papel" label="Nosso papel"
              rules={[{ required: true, message: 'Diga de que lado a Hydrodata está' }]}
              style={{ flex: '1 1 200px' }}>
              <Select placeholder="Escolha" options={opcoesPapel(tipoNovo)} />
            </Form.Item>
            <Form.Item name="valor" label="Valor (R$)" style={{ flex: '0 0 160px' }}>
              <InputNumber style={{ width: '100%' }} min={0} decimalSeparator="," />
            </Form.Item>
          </Flex>
          <Flex gap={12} wrap>
            <Form.Item name="vigencia_inicio" label="Vigência — início" style={{ flex: '0 0 160px' }}>
              <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="vigencia_fim" label="Vigência — fim" style={{ flex: '0 0 160px' }}>
              <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="renovacao" label="Renovação" initialValue="nao" style={{ flex: '1 1 200px' }}>
              <Select options={Object.entries(RENOVACAO_CFG).map(([v, l]) => ({ value: v, label: l }))} />
            </Form.Item>
          </Flex>
        </Form>
      </Modal>

      {/* ── Ficha ── */}
      <Modal
        open={!!ficha} onCancel={() => setFicha(null)} onOk={salvar}
        confirmLoading={salvando} okText="Salvar" cancelText="Cancelar"
        title={ficha ? `Ficha — ${ficha.contraparte_nome || ficha.codigo}` : ''}
        width="94vw" style={{ maxWidth: 760, top: 16 }}
        styles={{ body: { maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' } }}
      >
        <Form form={formFicha} layout="vertical" size="small">
          <Flex gap={12} wrap>
            <Form.Item name="contraparte_nome" label="Contraparte" rules={[{ required: true }]}
              style={{ flex: '1 1 260px' }}>
              <Input />
            </Form.Item>
            <Form.Item name="contraparte_doc" label="CNPJ / CPF" style={{ flex: '0 0 180px' }}>
              <Input />
            </Form.Item>
          </Flex>
          <Form.Item name="objeto" label="Objeto"><Input.TextArea rows={2} /></Form.Item>
          <Flex gap={12} wrap>
            <Form.Item name="nosso_papel" label="Nosso papel" style={{ flex: '1 1 200px' }}>
              <Select options={opcoesPapel(ficha)} />
            </Form.Item>
            <Form.Item name="valor" label="Valor (R$)" style={{ flex: '0 0 160px' }}>
              <InputNumber style={{ width: '100%' }} min={0} decimalSeparator="," />
            </Form.Item>
            <Form.Item name="situacao" label="Situação" style={{ flex: '0 0 170px' }}>
              <Select options={['rascunho', 'vigente', 'encerrado', 'rescindido']
                .map((v) => ({ value: v, label: SITUACAO_CFG[v]?.label || v }))} />
            </Form.Item>
          </Flex>
          <Flex gap={12} wrap>
            <Form.Item name="vigencia_inicio" label="Vigência — início" style={{ flex: '0 0 160px' }}>
              <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="vigencia_fim" label="Vigência — fim" style={{ flex: '0 0 160px' }}>
              <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="renovacao" label="Renovação" style={{ flex: '1 1 200px' }}>
              <Select options={Object.entries(RENOVACAO_CFG).map(([v, l]) => ({ value: v, label: l }))} />
            </Form.Item>
          </Flex>
          <Flex gap={12} wrap>
            <Form.Item name="aviso_previa_dias" label="Avisar quantos dias antes do fim"
              tooltip="É o que decide quando o contrato aparece no e-mail de segunda-feira."
              style={{ flex: '0 0 220px' }}>
              <InputNumber style={{ width: '100%' }} min={0} max={365} />
            </Form.Item>
            <Form.Item name="reajuste_indice" label="Índice de reajuste" style={{ flex: '0 0 160px' }}>
              <Select allowClear options={['IPCA', 'IGPM', 'INCC', 'INPC']
                .map((v) => ({ value: v, label: v }))} />
            </Form.Item>
            <Form.Item name="foro" label="Foro" style={{ flex: '1 1 200px' }}>
              <Input placeholder="Porto Alegre/RS" />
            </Form.Item>
          </Flex>
          <Form.Item name="observacoes" label="Observações"><Input.TextArea rows={2} /></Form.Item>
          {/* GUARDA-CHUVA (2026-08-25): o mesmo papel cobre vários projetos —
              o contrato da Nova Engevix cita 5 propostas de uma vez. Editável
              aqui porque o instrumento chega meses depois, quando os aditivos
              já existem. O primeiro escolhido vira o "projeto principal" da
              ficha; esvaziar a lista NÃO desfaz a cobertura (o backend só
              mexe quando a lista vem preenchida). */}
          <Form.Item name="cobre_contratos_op"
            label={<>Projetos cobertos por este contrato{' '}
              <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                (um instrumento pode cobrir o contrato e os aditivos)
              </Typography.Text></>}>
            <Select mode="multiple" allowClear showSearch optionFilterProp="label"
              loading={carregandoCob}
              placeholder={carregandoCob ? 'Carregando…' : 'Nenhum projeto vinculado'}
              options={opcoesOp.map((o) => ({
                value: o.id,
                label: `${o.numero} — ${o.titulo}`,
              }))} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
